/**
 * SIGNOUT-BROADCAST-1 — when the session ends in one tab, every open tab of
 * this origin forgets it and goes to sign-in.
 *
 * "Ended" means: the user signed out, or the server refused a renewal (the
 * session expired or was revoked). A plain 401 that renews successfully is NOT
 * an end (#1570 / #1571 renew first), so it is never announced.
 *
 * Transport: BroadcastChannel, plus a `storage`-event fallback for browsers
 * without it. The storage entry is a timestamped marker only — never a token.
 * Receivers act at most once and never re-announce, so nothing can loop.
 */

export type SessionEndReason = 'signout' | 'expired';

type SessionEndedMessage = { type: 'session-ended'; from: string; reason: SessionEndReason; at: number };

export const SESSION_CHANNEL_NAME = 'zephix-session';
export const SESSION_STORAGE_KEY = 'zephix.session-ended';

function newTabId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  return c?.randomUUID ? c.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

const tabId = newTabId();
let channel: BroadcastChannel | null | undefined;

function getChannel(): BroadcastChannel | null {
  if (channel !== undefined) return channel;
  channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(SESSION_CHANNEL_NAME) : null;
  // Node's BroadcastChannel keeps the process alive; browsers have no unref.
  (channel as { unref?: () => void } | null)?.unref?.();
  return channel;
}

function isEndedMessage(value: unknown): value is SessionEndedMessage {
  const m = value as Partial<SessionEndedMessage> | null;
  return !!m && m.type === 'session-ended' && typeof m.from === 'string';
}

/** Tell every other tab of this origin that the session has ended. */
export function announceSessionEnded(reason: SessionEndReason): void {
  const msg: SessionEndedMessage = { type: 'session-ended', from: tabId, reason, at: Date.now() };
  try {
    getChannel()?.postMessage(msg);
  } catch {
    // A closed channel must not block sign-out.
  }
  try {
    // A new value each time, so the storage event always fires in other tabs.
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(msg));
  } catch {
    // Private mode can refuse storage; BroadcastChannel still carries it.
  }
}

/**
 * Run `onEnded` once when ANOTHER tab announces the end of the session.
 * Returns an unsubscribe function.
 */
export function onSessionEndedElsewhere(onEnded: (reason: SessionEndReason) => void): () => void {
  let handled = false;
  const handle = (value: unknown): void => {
    if (handled || !isEndedMessage(value) || value.from === tabId) return;
    handled = true;
    onEnded(value.reason);
  };

  const ch = getChannel();
  const onChannel = (ev: MessageEvent): void => handle(ev.data);
  ch?.addEventListener('message', onChannel);

  const onStorage = (ev: StorageEvent): void => {
    if (ev.key !== SESSION_STORAGE_KEY || !ev.newValue) return;
    try {
      handle(JSON.parse(ev.newValue));
    } catch {
      // Not ours / malformed: ignore.
    }
  };
  if (typeof window !== 'undefined') window.addEventListener('storage', onStorage);

  return () => {
    ch?.removeEventListener('message', onChannel);
    if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
  };
}
